import { useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import getConfig from "next/config";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/button";
import { ContentLibraryDialog } from "@/components/shared/content-library/content-library-dialog";
import { ContentLibrary } from "@/components/shared/content-library/content-library-loader";
import { useContentLibraryTranslations } from "@/components/shared/content-library/use-content-library-translations";
import { fetchRecordingsById } from "@/features/recordings/recordings-reducer";
import useAnalytics from "@/hooks/use-analytics";
import useEvent from "@/hooks/use-event";
import { useAppDispatch } from "@/redux/store";

import type { VideoGridProps } from "../video-grid-component";

const { publicRuntimeConfig } = getConfig();

export const LibrarySetting = () => {
  const { t, i18n } = useTranslation();
  const {
    event: { organization, space_external_id },
  } = useEvent();
  const { preEventOrganizationSpaces: spacesEnabled } = useFlags();
  const dispatch = useAppDispatch();
  const { logAnalytics } = useAnalytics();
  const translations = useContentLibraryTranslations();

  const spaceExternalId = spacesEnabled
    ? space_external_id ?? undefined
    : undefined;

  const {
    props,
    actions: { setProp },
  } = useNode((query) => {
    const filteredVideoList = query.props.videoList.filter((video) => {
      return typeof video !== "string";
    });
    return {
      props: {
        ...(query.props as VideoGridProps),
        videoList: filteredVideoList,
      },
    };
  });

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const selectedRecordingIds = props.videoList
    .map((video) => {
      return video.recordingId;
    })
    .filter(Boolean);

  return (
    <div className="grid gap-4">
      <Button
        size="medium"
        onClick={() => {
          setIsLibraryOpen(true);
          logAnalytics("CANVAS_CONTENT_LIBRARY_OPENED");
        }}
      >
        {t("preference-panel.video-grid-choose-videos")}
      </Button>
      <ContentLibraryDialog
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        unmount={true}
      >
        <ContentLibrary
          options={{
            host: publicRuntimeConfig.HOPIN_ROOT,
            organisationId: organization.external_id,
            spaceExternalId,
            i18n: {
              locale: i18n.language,
              translations,
            },
          }}
          onClose={() => setIsLibraryOpen(false)}
          allowSingleSelection={false}
          selectedRecordingIds={selectedRecordingIds}
          onConfirmSelection={async (recordingIds) => {
            try {
              await dispatch(
                fetchRecordingsById({
                  organizationExternalId: organization.external_id,
                  recordingIds: recordingIds,
                  spaceExternalId,
                })
              ).unwrap();

              setProp((props: VideoGridProps) => {
                const embededVideos = props.videoList.reduce(
                  (videos, currentVideo) => {
                    if (
                      typeof currentVideo === "string" ||
                      currentVideo.kind === "recording"
                    ) {
                      return videos;
                    }
                    return [...videos, currentVideo];
                  },
                  []
                );
                const recordings = recordingIds.map((recordingId) => ({
                  kind: "recording",
                  recordingId,
                }));
                props.videoList = [...recordings, ...embededVideos];
              });
            } catch {
              // error handled inside recordings reducer slice
            }
          }}
        />
      </ContentLibraryDialog>
    </div>
  );
};
